import { collection, doc, serverTimestamp, updateDoc, writeBatch, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { createLocalDoc, fireWrite } from "./offlineWrites";
import type { EntityType, TimeInputMode } from "../types";
import { builtInTimeInputMode } from "../config/typeSettings";

/**
 * Attaching activities that were logged as free text to a real place.
 *
 * An activity with no `entityId` is a dead end: it can't roll up to a venue,
 * it never meets the same night at the same bar from another trip, and the
 * catalog has no idea it happened. Linking is the whole repair — the activity
 * keeps everything it recorded and simply gains the place it belongs to.
 *
 * `name` is deliberately left alone. It's what someone actually wrote down
 * ("Dinner at Space 220", "Hooks"), and every view already prefers the linked
 * entity's name over it, so keeping it costs nothing and preserves how the
 * entry was originally logged.
 */

/** Firestore caps a batch at 500 writes. */
const BATCH_LIMIT = 450;

/**
 * Point activities at an entity. Fire-and-forget, in line with the rest of the
 * app's writes: the change is in the local cache immediately and syncs on its
 * own, so the caller never waits on a server round-trip that may not come.
 */
export function linkInteractionsToEntity(interactionIds: string[], entityId: string): void {
  const ids = [...new Set(interactionIds)].filter(Boolean);
  if (ids.length === 0) return;

  // One activity is the common case — a plain update reads better in the
  // Firestore console and skips the batch machinery entirely.
  const [only] = ids;
  if (ids.length === 1 && only) {
    fireWrite(
      updateDoc(doc(db, "interactions", only), {
        entityId,
        // Linking answers the question the flag was suppressing.
        noPlace: false,
        updatedAt: serverTimestamp(),
      }),
      "link activity to entity",
    );
    return;
  }

  for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const id of ids.slice(i, i + BATCH_LIMIT)) {
      batch.update(doc(db, "interactions", id), { entityId, noPlace: false, updatedAt: serverTimestamp() });
    }
    fireWrite(batch.commit(), `link ${ids.length} activities to entity`);
  }
}

/**
 * Mark activities as deliberately place-less, or put them back in the queue.
 *
 * Plenty of what gets logged is not a venue — "Breakfast" thirteen times, the
 * safety drill, a beach afternoon, the flight home. Linking those to something
 * would be a lie, but leaving them unanswered means the backlog never empties
 * and the entries that *do* need a place stay buried among them. This is the
 * third answer: no, and stop asking.
 */
export function setActivitiesNoPlace(interactionIds: string[], noPlace: boolean): void {
  const ids = [...new Set(interactionIds)].filter(Boolean);
  if (ids.length === 0) return;

  const [only] = ids;
  if (ids.length === 1 && only) {
    fireWrite(
      updateDoc(doc(db, "interactions", only), { noPlace, updatedAt: serverTimestamp() }),
      "mark activity as having no place",
    );
    return;
  }

  for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const id of ids.slice(i, i + BATCH_LIMIT)) {
      batch.update(doc(db, "interactions", id), { noPlace, updatedAt: serverTimestamp() });
    }
    fireWrite(batch.commit(), `mark ${ids.length} activities as having no place`);
  }
}

/** Detach an activity from its place, putting it back in the unlinked pile. */
export function unlinkInteraction(interactionId: string): void {
  fireWrite(
    updateDoc(doc(db, "interactions", interactionId), {
      entityId: null,
      updatedAt: serverTimestamp(),
    }),
    "unlink activity from entity",
  );
}

/**
 * What an entity should ask for when logging against it, by type. Mirrors the
 * fallback in `getTimeInputMode` so a promoted place behaves like one created
 * by hand with the form's defaults.
 */
export function defaultTimeInputMode(type: EntityType): TimeInputMode {
  return builtInTimeInputMode(type);
}

export interface NewEntityFromActivity {
  displayName: string;
  type: EntityType;
  parentId: string | null;
}

/**
 * Create a catalog entity out of what an activity recorded, returning its id
 * synchronously so the caller can link straight to it (works offline — see
 * `createLocalDoc`).
 *
 * The entity is deliberately sparse: a name, a type, and where it sits. Images,
 * wiki links and event options are left for the entity's own edit form rather
 * than guessed at here.
 */
export function createEntityFromActivity({ displayName, type, parentId }: NewEntityFromActivity): string {
  return createLocalDoc(collection(db, "entities"), {
    displayName: displayName.trim(),
    type,
    parentId,
    imageUrl: null,
    externalId: null,
    externalSource: null,
    activeFrom: null,
    activeTo: null,
    customConfig: { time_input_mode: defaultTimeInputMode(type) },
    activityOptions: [],
    eventOptions: [],
    location: null,
    createdAt: Timestamp.now(),
    updatedAt: serverTimestamp(),
  });
}
